import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva } from 'class-variance-authority';
import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { WithChildren } from '../../../types/component.types';
import { Icon } from '../../Icon/Icon';

type IsWithinParentSelector = {
  parentHandlesInteraction: true;
  label?: never;
};

type IsNotWithinParentSelector = {
  parentHandlesInteraction?: false;
  label?: string;
};

type CheckboxProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'className' | 'onChange'
> & {
  label?: string;
  indeterminate?: boolean;
  defaultChecked?: boolean;
  /**
   * Controlled value. Prefer this over `defaultChecked` whenever the value is
   * owned by something else (react-hook-form, a parent's state).
   * `defaultChecked` is read once on mount, so a value that arrives later is
   * silently ignored.
   */
  checked?: CheckboxPrimitive.CheckboxProps['checked'];
  parentHandlesInteraction?: boolean;
  onChange?: CheckboxPrimitive.CheckboxProps['onCheckedChange'];
} & Partial<WithChildren> &
  (IsWithinParentSelector | IsNotWithinParentSelector);

const checkboxItem = cva('justify-center rounded-xs h-[18px] w-[18px]', {
  variants: {
    parentHandlesInteraction: {
      true: 'focus-visible:outline-hidden',
    },
    disabled: {
      // A disabled box still has to show whether it is checked. Without the
      // checked fill both states render identically, so a locked-on checkbox
      // reads as unchecked. `surface-disabled-control` is the token for a
      // disabled control in its selected state - see RadioField.
      true: 'cursor-not-allowed bg-surface-disabled rdx-state-checked:bg-surface-disabled-control',
      false:
        'rdx-state-checked:bg-primary rdx-state-unchecked:border-2 rdx-state-unchecked:border-strong',
    },
  },
  defaultVariants: {
    parentHandlesInteraction: false,
    disabled: false,
  },
  compoundVariants: [
    {
      parentHandlesInteraction: false,
      disabled: false,
      class:
        'active:h-[24px] active:w-[24px] focus:ring-3 focus:ring-focus rdx-state-unchecked:bg-surface',
    },
    {
      parentHandlesInteraction: true,
      disabled: true,
      class: 'rdx-state-unchecked:border-2 rdx-state-unchecked:border-strong',
    },
  ],
});

const checkboxItemWrapper = cva('flex justify-center items-center', {
  variants: {
    parentHandlesInteraction: {
      false: 'py-xs px-xxs gap-xs min-h-[40px] min-w-[40px]',
    },
    disabled: {
      false: '',
    },
  },
  defaultVariants: {
    parentHandlesInteraction: false,
    disabled: false,
  },
  compoundVariants: [
    {
      parentHandlesInteraction: false,
      disabled: false,
      class: 'rounded-circle hover:bg-secondary-hover',
    },
  ],
});

// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
export const Checkbox = forwardRef<
  ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      id: idProp,
      children,
      label,
      indeterminate,
      defaultChecked,
      checked,
      disabled = false,
      parentHandlesInteraction = false,
      onChange,
      ...props
    },
    forwardedRef,
  ) => {
    const _id = useId();
    const id = idProp || _id;

    return (
      <div className="flex items-center">
        <div
          className={checkboxItemWrapper({
            parentHandlesInteraction,
            disabled,
          })}
        >
          <CheckboxPrimitive.Root
            {...props}
            id={id}
            defaultChecked={defaultChecked}
            checked={checked}
            onClick={disabled ? (e) => e.preventDefault() : props.onClick}
            onCheckedChange={(state) => onChange && onChange(state)}
            ref={forwardedRef}
            className={twMerge(
              checkboxItem({ parentHandlesInteraction, disabled }),
            )}
            aria-disabled={disabled}
          >
            <CheckboxPrimitive.Indicator className="w-l text-surface">
              <Icon name={indeterminate ? 'remove' : 'check'} size="medium" />
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>
        </div>
        {label && !children && !parentHandlesInteraction && (
          <LabelPrimitive.Label
            htmlFor={id}
            className={twMerge('text-label', !disabled && 'cursor-pointer')}
          >
            {label}
          </LabelPrimitive.Label>
        )}
        {children}
      </div>
    );
  },
);
Checkbox.displayName = 'Checkbox';
